/**
 * @file src/helpers/getRequestFailReason.ts
 * @exports getRequestFailReason, getResponseStatus
 * @summary Classifies a failed request into a bounded analytics reason, without reading any error text.
 * @remarks Follows `cause`, because the GraphQL fetcher wraps the Axios error it received.
 */

import axios, { type AxiosError } from "axios";
import type { AnalyticsRequestFailReason } from "./analyticsEvents";

const MAX_CAUSE_DEPTH = 5;

const findAxiosError = (error: unknown): AxiosError | undefined => {
	let current = error;
	for (let depth = 0; depth < MAX_CAUSE_DEPTH && current; depth++) {
		if (axios.isAxiosError(current)) return current;
		current = (current as { cause?: unknown }).cause;
	}
	return undefined;
};

/** The HTTP status of a failed request, if it got a response. */
export const getResponseStatus = (error: unknown): number | undefined => findAxiosError(error)?.response?.status;

/** `network_error` when no response arrived, `server_error` for a 5xx, otherwise `unknown`. */
export const getRequestFailReason = (error: unknown): AnalyticsRequestFailReason => {
	const axiosError = findAxiosError(error);
	if (!axiosError || axiosError.code === "ERR_CANCELED") return "unknown";
	const status = axiosError.response?.status;
	if (status === undefined) return "network_error";
	return status >= 500 ? "server_error" : "unknown";
};
